import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from 'axios';
import Nav from "./Nav";
import Footer from "./Footer";
import { HiLockClosed, HiEye, HiEyeOff, HiArrowRight } from "react-icons/hi";

function ResetPassword() {
  const navigate = useNavigate();
  const BASE_URL = `${process.env.REACT_APP_API_URL || `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}`}/api/`;
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: localStorage.getItem("userEmail") || "",
    password: "",
    confPassword: "",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const userEmail = formData.email || localStorage.getItem("userEmail");
    if (!userEmail) {
      toast.error("Session expired. Please request password reset again.");
      navigate("/forgot-password");
      return;
    }

    if (!formData.password.trim() || formData.password.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return;
    }

    if (formData.password !== formData.confPassword) {
      toast.error("New Password and Confirm New Password do not match");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.patch(`${BASE_URL}reset-password`, {
        email: userEmail,
        password: formData.password,
      });

      if (response.status === 200 || response.data.message) {
        toast.success("Password reset successfully! Please log in.");
        localStorage.removeItem("emailVerified");
        localStorage.removeItem("userEmail");
        navigate("/login");
      } else {
        toast.error(response.data.message || "Failed to reset password");
      }
    } catch (error) {
      console.error("Error:", error.message);
      const errMsg = error.response?.data?.message || "Failed to reset password";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Nav />

      <main className="flex-grow flex items-center justify-center p-4 pt-32 pb-20">
        <div className="w-full max-w-[450px] animate-in fade-in zoom-in duration-500">
          <div className="glass rounded-3xl p-8 md:p-10 shadow-2xl relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-600/10 rounded-full blur-3xl"></div>

            <div className="relative z-10">
              <div className="text-center mb-8">
                <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] font-black uppercase tracking-widest mb-2 border border-blue-100">
                  Account Recovery
                </span>
                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                  Reset Password
                </h1>
                <p className="text-slate-500 text-xs">
                  Create a new password to secure your account.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* New Password Field */}
                <div className="space-y-1.5">
                  <label htmlFor="password" className="text-sm font-semibold text-slate-700 ml-1">
                    New Password
                  </label>
                  <div className="relative group">
                    <HiLockClosed className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors text-xl" />
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className="input-field pl-12 pr-12"
                      autoFocus
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? (
                        <HiEyeOff size={20} />
                      ) : (
                        <HiEye size={20} />
                      )}
                    </button>
                  </div>
                </div>

                {/* Confirm New Password Field */}
                <div className="space-y-1.5">
                  <label htmlFor="confPassword" className="text-sm font-semibold text-slate-700 ml-1">
                    Confirm New Password
                  </label>
                  <div className="relative group">
                    <HiLockClosed className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors text-xl" />
                    <input
                      type={showPassword ? "text" : "password"}
                      id="confPassword"
                      name="confPassword"
                      value={formData.confPassword}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className="input-field pl-12 pr-12"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-3.5 mt-2 flex items-center justify-center gap-2 group relative overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <span>Reset Password</span>
                      <HiArrowRight className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-slate-200/60 text-center">
                <Link
                  to="/login"
                  className="text-xs font-bold text-slate-500 hover:text-blue-600 transition-colors"
                >
                  Back to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default ResetPassword;
